<!--@format-->
<template>
    <div class="partner-assign-users-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Assign a User</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="loading" class="loading-card">
                    <loading-screen-svg />
                </div>
                <div v-else class="bordered - container">
                    <div class="title">Choose Users</div>
                    <div v-if="showAccountManagers" class="input-container">
                        <agency-users-search
                            :options="getOptions()"
                            :selected="assignedAccountManagers"
                            @assign="assignAccountManager($event)"
                            @remove="removeAccountManager($event)"
                        />
                    </div>
                    <div v-else class="input-container">
                        <agency-users-search
                            :options="getOptions()"
                            :selected="assignedUsers"
                            @assign="assignUser($event)"
                            @remove="removeUser($event)"
                        />
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button :disabled="isSaving" @click="save()">Save</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AgencyUsersSearch = () => import('@/components/client/agency-users-search.vue');
const AppButton = () => import('@/components/common/app-button.vue');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import { differenceBy } from 'lodash';

export default {
    name: 'partner-assign-users-widget',

    components: {
        'agency-users-search': AgencyUsersSearch,
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: true,
            isSaving: false,
            clientName: null,

            availableUsers: [],
            assignedUsers: [],
            assignedAccountManagers: [],

            agencyId: this.widget.agencyId,
            agencyClientId: this.widget.agencyClientId,
            clientAccountId: this.widget.clientAccountId,
            showAccountManagers: this.widget.showAccountManagers,
        };
    },

    created() {
        const promises = [this.getUsers(), this.getAssignedUsers()];

        this.loading = true;
        Promise.all(promises)
            .catch((error) => {
                this.$helpers.showFailNotification('An error occurred while fetching users.', {
                    error,
                });
            })
            .finally(() => {
                this.loading = false;
            });
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getOptions() {
            return differenceBy(this.availableUsers, this.assignedUsers, 'id');
        },

        save() {
            this.isSaving = true;
            const changes = {
                AgencyClientId: this.agencyClientId,
                Users: this.assignedUsers.map(({ id }) => ({ UserId: id })),
            };

            var successMessage = '';

            if (this.showAccountManagers) {
                if (this.assignedAccountManagers.length === 0) {
                    successMessage = `Removed all account managers from ${this.clientName || ''}`;
                } else if (this.assignedAccountManagers.length > 1) {
                    successMessage = `<b>${this.assignedAccountManagers[0].name}</b> and <b>${this.assignedAccountManagers.length - 1}</b> other users were assigned to <b>${this.clientName || ''}</b>`;
                } else {
                    successMessage = `<b>${this.assignedAccountManagers[0].name}</b> was assigned to <b>${this.clientName || ''}</b>`;
                }
            } else {
                if (this.assignedUsers.length === 0) {
                    successMessage = `Removed all assigned users from ${this.clientName || ''}`;
                } else if (this.assignedUsers.length > 1) {
                    successMessage = `<b>${this.assignedUsers[0].name}</b> and <b>${this.assignedUsers.length - 1}</b> other users were assigned to <b>${this.clientName || ''}</b>`;
                } else {
                    successMessage = `<b>${this.assignedUsers[0].name}</b> was assigned to <b>${this.clientName || ''}</b>`;
                }
            }

            this.$api
                .post('/api/AgencyClients/AssignUser', changes)
                .then(() => {
                    this.$helpers.showSuccessNotification(successMessage);
                    EventBus.$emit('agency-client:update');
                    EventBus.$emit('close-widget', this.widget);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while saving your information.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        getUsers() {
            const filter = `$filter=AgencyId eq ${this.agencyId} and IsActive eq true`;
            const select = '$select=Id,FirstName,LastName,ProfileImageUrl,Title,Roles';
            const query = `/service/Users?${filter}&${select}`;

            this.$api.get(query).then((response) => {
                if (this.showAccountManagers) {
                    var accountManagers = response.data.value.filter((item) =>
                        item.Roles.some((r) => r.RoleId === '16'),
                    );
                    this.availableUsers = accountManagers.map(this.mapAgencyUserToOption);
                } else {
                    this.availableUsers = response.data.value.map(this.mapAgencyUserToOption);
                }
            });
        },

        mapAgencyUserToOption(user) {
            return {
                id: user.Id,
                name: `${user.FirstName} ${user.LastName}`,
                profilePicture: user.ProfileImageUrl,
                title: user.Title,
            };
        },

        getFilterParam() {
            if (this.agencyClientId) {
                return `$filter=Id eq ${this.agencyClientId}`;
            } else {
                return `$filter=AgencyId eq ${this.agencyId} and Client/Accounts/any(a: a/Id eq ${this.clientAccountId})`;
            }
        },

        getSelectParam() {
            return '$select=Id';
        },

        getExpandParam() {
            return '$expand=AgencyClientUsers($expand=User($select=FirstName,LastName,Title,Id,Roles,ProfileImageUrl)),Client($select=Name)';
        },

        getQueryParams() {
            const optionsQuery = [
                this.getExpandParam(),
                this.getSelectParam(),
                this.getFilterParam(),
            ].join('&');
            this.optionsQuery = optionsQuery;
            return optionsQuery;
        },

        getAssignedUsers() {
            let query = `/service/AgencyClients?${this.getQueryParams()}`;
            this.$api.get(query).then((response) => {
                this.assignedUsers = response.data.value[0].AgencyClientUsers.map(
                    this.mapAssignedUsers,
                );

                this.clientName = response.data.value[0].Client.Name;
                this.agencyClientId = response.data.value[0].Id;

                if (this.showAccountManagers) {
                    this.assignedAccountManagers = this.assignedUsers.filter((user) =>
                        user.roleIds.includes('16'),
                    );
                }
            });
        },

        mapAssignedUsers(item) {
            return {
                id: item.User.Id,
                name: `${item.User.FirstName} ${item.User.LastName}`,
                profilePicture: item.User.ProfileImageUrl,
                title: item.User.Title,
                roleIds: item.User.Roles.map((r) => {
                    return r.RoleId;
                }),
            };
        },

        assignUser(userToAssign) {
            this.assignedUsers.push(userToAssign);
        },

        removeUser(userToRemove) {
            this.assignedUsers = this.assignedUsers.filter(({ id }) => id !== userToRemove.id);
        },

        assignAccountManager(userToAssign) {
            this.assignedAccountManagers.push(userToAssign);
            this.assignedUsers.push(userToAssign);
        },

        removeAccountManager(userToRemove) {
            this.assignedAccountManagers = this.assignedAccountManagers.filter(
                ({ id }) => id !== userToRemove.id,
            );
            this.assignedUsers = this.assignedUsers.filter(({ id }) => id !== userToRemove.id);
        },
    },
};
</script>

<style>
.partner-assign-users-widget .form-group {
    margin-top: 1em;
}

.partner-assign-users-widget .app-input {
    margin-bottom: 1em;
}

.partner-assign-users-widget .bordered-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    margin: 25px 0;
    padding: 10px 20px;
    position: relative;
}

.partner-assign-users-widget .bordered-container .title {
    background-color: #ffffff;
    font-weight: 600;
    left: 50%;
    margin-left: -82px;
    padding: 0px 25px;
    position: absolute;
    top: -11px;
}

.partner-assign-users-widget .bordered-container .loading-card {
    text-align: center;
}

.partner-assign-users-widget .bordered-container .input-container {
    margin: 1.5em 0;
}

.partner-assign-users-widget .assigned-users {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
}

.partner-assign-users-widget .assigned-users .user-chips {
    align-items: center;
    border: 1px solid #cac6cb;
    border-radius: 15px;
    display: flex;
    font-size: 12px;
    gap: 8px;
    padding: 3px 10px;
}

.partner-assign-users-widget .assigned-users .user-chips img {
    border-radius: 50%;
    height: 20px;
    width: 20px;
}

.partner-assign-users-widget .assigned-users .user-chips svg {
    cursor: pointer;
    margin-left: 12px;
    width: 10px;
}

.partner-assign-users-widget .cancel-btn {
    background: transparent;
    border: none;
    color: #877d88;
}

.partner-assign-users-widget .cancel-btn:hover {
    background: #e9e9ef;
}
</style>
